import { computed, ref } from 'vue'

// Quem deixa a aba aberta a semana toda pode operar dias numa versão antiga sem saber.
// Dez minutos é frequente o bastante para avisar no mesmo dia do deploy e raro o
// bastante para não pesar: é uma requisição a um arquivo de três linhas.
const INTERVALO_DE_CHECAGEM_MS = 10 * 60 * 1000

// Injetada no bundle pelo Vite (ver define em vite.config.js). É a única fonte confiável
// para "qual versão este código é": o localStorage só sabe o que a visita anterior viu,
// e comparar com ele acusava versão nova para quem tinha acabado de recebê-la.
const versaoEmUso = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'desconhecida'

const avisoVisivel = ref(false)

let versaoPublicadaConhecida = null
let versaoDispensada = null
let monitorando = false

async function lerVersaoPublicada() {
  try {
    const resposta = await fetch(`/version.js?cache=${Date.now()}`, { cache: 'no-store' })

    if (!resposta.ok) {
      return null
    }

    const dados = await resposta.json()

    return typeof dados?.version === 'string' ? dados.version : null
  } catch {
    // Sem rede, ou servidor momentaneamente fora: não é problema do usuário e não muda
    // nada do que ele está fazendo. Tentamos de novo na próxima checagem.
    return null
  }
}

/**
 * Compara a versão publicada com a que esta aba está executando.
 */
export async function verificarNovaVersao() {
  const publicada = await lerVersaoPublicada()

  if (!publicada) {
    return
  }

  versaoPublicadaConhecida = publicada

  if (publicada === versaoDispensada) {
    return
  }

  // Em desenvolvimento a versão do bundle não é gerada, então não há o que comparar
  if (versaoEmUso === 'desenvolvimento' || versaoEmUso === 'desconhecida') {
    return
  }

  avisoVisivel.value = publicada !== versaoEmUso
}

/**
 * Começa a acompanhar novas versões. Seguro chamar mais de uma vez.
 */
export function monitorarNovaVersao() {
  if (monitorando) {
    return
  }

  monitorando = true

  verificarNovaVersao()
  setInterval(verificarNovaVersao, INTERVALO_DE_CHECAGEM_MS)

  // Voltar para a aba é o momento em que a pessoa retoma o trabalho: melhor descobrir
  // que há versão nova agora do que no meio de um preenchimento.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      verificarNovaVersao()
    }
  })
}

/**
 * Recarrega para receber a versão nova. Só é chamada quando o usuário pede.
 *
 * A recarga passa de propósito pela guarda de alterações não salvas: quem clicar aqui
 * com um formulário preenchido precisa ser avisado antes de perder o trabalho.
 */
export function aplicarNovaVersao() {
  window.location.reload()
}

/**
 * Adia o aviso. Ele volta quando sair um deploy mais novo que o recusado agora.
 */
export function dispensarAviso() {
  versaoDispensada = versaoPublicadaConhecida
  avisoVisivel.value = false
}

export function useNovaVersao() {
  return {
    versaoEmUso,
    novaVersaoDisponivel: computed(() => avisoVisivel.value),
    aplicarNovaVersao,
    dispensarAviso
  }
}
